import angular from 'angular';
import $ from 'jquery';

import { logger } from '../services/logger';

PpnPublicCtrl.$inject = [
  '$rootScope',
  '$timeout',
  '$scope',
  '$compile',
  'ProjectPart',
  'SvgUpload',
  '$http',
];

/**
 * @typedef {import('../types').RootScope} RootScope
 * @typedef {import('./types').ProjectPart} ProjectPart
 * @typedef {import('../svg.upload/types').SvgUpload} SvgUpload
 */

/**
 * @typedef {object} PpnPublicCtrlProps
 * @property {number=} houseId - will be set if rendered from directive
 * @property {string=} svgUrl - optional url that can be passed from outside skip additional requests
 * @property {string=} imageUrl - optional url that can be passed from outside skip additional requests
 * @property {boolean} showProjectPart
 * @property {UiProjectPart[]} projectParts
 * @property {UiProjectPart=} projectPartData
 * @property {boolean} showPartInfoPopup
 * @property {(partId: string) => void} showShortInfo
 * @property {() => void} hideShortInfo
 * @property {() => void} hideFoucSpinner
 * @property {(partId: string) => void} showPartPopup
 * @property {(partId: string) => void} showPartPopupOnTouch
 * @property {(rawPartId: string) => {[key: string]: boolean}} getPartClasses
 * @property {(rawPartId: string, type: string, className: string | true) => boolean} checkPartClass
 *
 * @typedef {ProjectPart & {tapped?: boolean}} UiProjectPart
 */

/**
 * @param {RootScope} $rootScope
 * @param {angular.ITimeoutService} $timeout
 * @param {PpnPublicCtrlProps & angular.IScope} $scope
 * @param {angular.ICompileService} $compile
 * @param {any} ProjectPart
 * @param {SvgUpload} SvgUpload
 * @param {angular.IHttpService} $http
 */
export function PpnPublicCtrl(
  $rootScope,
  $timeout,
  $scope,
  $compile,
  ProjectPart,
  SvgUpload,
  $http,
) {
  $scope.showProjectPart = false;
  $scope.projectParts = [];
  $scope.showPartInfoPopup = false;
  const isTouchDevice = is_touch_device();
  const windowWidth = $(window).innerWidth();
  const query = new URLSearchParams(window.location.search);

  if (query.get('layout') === 'wide') {
    // switch to new wide layout that removes extra whitespace and allows
    // direct embedding of iframe on site
    $('html').addClass('ppn--wide');

    $scope.$on('$destroy', () => {
      $('html').removeClass('ppn--wide');
    });
  }

  const projectId =
    $scope.houseId || parseIdFromString(window.location.pathname);

  ProjectPart.getProjectParts({ id: projectId })
    .$promise.then((projectParts) => {
      $scope.projectParts = projectParts;
    })
    .catch((error) => {
      logger.error('Error loading parts data', {
        error,
        projectId,
      });

      throw error;
    });

  const newNaviPromise = (
    $scope.imageUrl && $scope.svgUrl
      ? Promise.resolve({
          finalImage: $scope.imageUrl,
          finalSvg: $scope.svgUrl,
        })
      : SvgUpload.fetchImages({
          houseId: projectId,
        })
  )
    .then(async ({ finalSvg, finalImage }) => {
      if (!finalSvg || !finalImage) {
        return;
      }

      // replace $scope.hideFoucSpinner with noop because we will preload images from js
      const hideSpinner = $scope.hideFoucSpinner.bind($scope);
      $scope.hideFoucSpinner = () => {};

      /** @type {{data: string}} */
      const { data: svgCode } = await $http.get(finalSvg);

      const $svg = angular.element(svgCode);
      const imageEl = $svg.find('image')[0];

      if (!imageEl) {
        throw new Error('Can not find image element in svg');
      }

      // replace xlink:href with correct image src
      imageEl.setAttribute('xlink:href', finalImage);

      const html = $compile($svg)(
        /** @type {PpnPublicCtrlProps & angular.IScope} */ ($scope),
      );

      angular.element('#project-part-svg-container').append(html);

      await new Promise((resolve, reject) => {
        const image = new Image();
        image.onload = resolve;
        image.onerror = reject;
        image.src = finalImage;
      });

      hideSpinner();

      return true;
    })
    .catch((error) => {
      logger.error('Error loading navi images', {
        error,
        projectId,
      });
    });

  if (!$scope.svgUrl && !$scope.imageUrl) {
    ProjectPart.getSvg({ id: projectId })
      .$promise.then(async (resp) => {
        const isNewNavi = await newNaviPromise;

        if (isNewNavi) {
          // skip legacy navi initialization because the new one was successfully initialized
          return;
        }

        const html = $compile(resp.svg)(
          /** @type {PpnPublicCtrlProps & angular.IScope} */ ($scope),
        );

        angular.element('#project-part-svg-container').append(html);
      })
      .catch((error) => {
        if (error.status === 404) {
          logger.warn('Project part svg not found', {
            error,
            projectId,
          });
        } else {
          logger.error('Error loading project part svg', {
            error,
            projectId,
          });
        }
      });
  }

  $scope.showShortInfo = (partId) => {
    const hoveredPartId = parseIdFromString(partId);

    const projectPartData = findPartById(hoveredPartId, $scope.projectParts);

    if (
      !projectPartData ||
      !['normal', 'incoming', 'sold'].includes(projectPartData.availability)
    ) {
      return;
    }

    if (isTouchDevice) {
      $scope.projectParts.forEach((item) => {
        item.tapped = false;
      });

      projectPartData.tapped = true;
    }

    $scope.showProjectPart = true;
    $scope.projectPartData = projectPartData;
  };

  $scope.hideShortInfo = () => {
    $scope.showProjectPart = false;

    if ($scope.projectPartData) {
      $scope.projectPartData.tapped = false;
    }
  };

  $scope.getPartClasses = (rawPartId) => ({
    part_incoming: $scope.checkPartClass(rawPartId, 'availability', 'incoming'),
    part_normal: $scope.checkPartClass(rawPartId, 'availability', 'normal'),
    part_sold: $scope.checkPartClass(rawPartId, 'availability', 'sold'),
    part_selected: $scope.checkPartClass(rawPartId, 'selected', true),
    part_featured: $scope.checkPartClass(rawPartId, 'featured', true),
    'part_class-standard': $scope.checkPartClass(
      rawPartId,
      'class',
      'standard',
    ),
    'part_class-improved': $scope.checkPartClass(
      rawPartId,
      'class',
      'improved',
    ),
    part_tapped: $scope.checkPartClass(rawPartId, 'tapped', true),
  });

  $scope.checkPartClass = (rawPartId, type, className) => {
    const partId = parseIdFromString(rawPartId);

    const part = findPartById(partId, $scope.projectParts);

    if (!part) {
      return false;
    }

    return part[type] === className;
  };

  $scope.showPartPopup = (partId) => {
    if (isTouchDevice) {
      $scope.showShortInfo(partId);

      return;
    }

    openSectionDetail(partId);
  };

  $scope.showPartPopupOnTouch = (partId) => {
    openSectionDetail(partId);
  };

  $scope.hideFoucSpinner = () => {
    if (windowWidth < 600) {
      scrollToCenter();
    }

    $timeout(() => {
      $rootScope.showNavFoucSpinner = false;
    }, 200);
  };

  function scrollToCenter() {
    const container = document.querySelector('#project-part-svg-container');

    if (!container) {
      return;
    }

    $timeout(() => {
      container.scrollLeft = container.scrollWidth / 2 - windowWidth / 2;
    }, 200);
  }

  /**
   * @param {string} partId
   *
   * @returns {void}
   */
  function openSectionDetail(partId) {
    const clickedPartId = parseIdFromString(partId);

    const projectPartData = findPartById(clickedPartId, $scope.projectParts);

    if (!projectPartData || projectPartData.availability !== 'normal') {
      return;
    }

    $scope.showPartInfoPopup = true;
    $scope.hideShortInfo();

    // send a message to a page from an iframe embedded within it.
    //$window.parent.postMessage('navi/detail/open', $location.protocol() + '://' + $location.host());
  }

  /**
   * @param {string} id
   * @param {UiProjectPart[]} projectParts
   *
   * @return {UiProjectPart | undefined}
   */
  function findPartById(id, projectParts) {
    return projectParts.find((item) => item.id === id);
  }

  /**
   * @param {string} rawId
   *
   * @return {string}
   */
  function parseIdFromString(rawId) {
    const id = rawId.match(/\d+/);

    if (!id) {
      throw new Error(`Can not parse number from string: ${rawId}`);
    }

    return id[0];
  }

  function is_touch_device() {
    const prefixes = ' -webkit- -moz- -o- -ms- '.split(' ');
    const mq = (/** @type {string} */ query) =>
      window.matchMedia(query).matches;

    if ('ontouchstart' in window) {
      return true;
    }

    // include the 'heartz' as a way to have a non matching MQ to help terminate the join
    // https://git.io/vznFH
    const query = ['(', prefixes.join('touch-enabled),('), 'heartz', ')'].join(
      '',
    );

    return mq(query);
  }
}
